import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { TokenStorageService } from '../../services/token-storage.service';
import { UserManagementService } from '../../services/user-management.service';
import { SnackbarService } from '../../services/snackbar.service';
import { AssetsService } from '../../services/assets/assets.service';
import { MatDialog } from '@angular/material/dialog';
import { MatDialogModule } from '@angular/material/dialog';
import { ConfirmPopupComponent } from '../../common/popups/confirm-popup/confirm-popup.component';
import { Destroyable } from '../../utils/mixins/destroyable.mixin';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { AUTH_PROVIDER_TOKEN, AuthProvider } from '../../core/tokens/auth-provider.token';
import { MsalService } from '@azure/msal-angular';

@Component({
  selector: 'app-sidenav',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatExpansionModule,
    MatDialogModule,
    RouterModule,
  ],
  templateUrl: './sidenav.component.html',
  styleUrl: './sidenav.component.scss',
})
export class SidenavComponent extends Destroyable(Object) implements OnInit {
  @Input() opened = true;
  @Output() openedChange = new EventEmitter<boolean>();
  notificationsCount = 0;
  userEmail!: string;

  constructor(
    private router: Router,
    private tokenStorageService: TokenStorageService,
    private userManagementService: UserManagementService,
    private snackbarService: SnackbarService,
    private assetsService: AssetsService,
    private dialog: MatDialog,
    @Inject(AUTH_PROVIDER_TOKEN) private authProvider: AuthProvider,
    @Inject(OKTA_AUTH) public oktaAuth: OktaAuth,
    private msalService: MsalService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.userEmail = this.userManagementService.getUserInfo()!.email;
  }

  logout(): void {
    this.userManagementService.removeUserInfo();

    if (this.authProvider === 'Entra') {
      this.msalService.logoutRedirect();
      return;
    }

    if (this.authProvider === 'Okta') {
      this.oktaAuth.signOut();
      return;
    }

    this.tokenStorageService.removeTokens();
    this.router.navigate(['login']);
  }

  toggle(): void {
    this.opened = !this.opened;
    this.openedChange.emit(this.opened);
  }

  openUserGuide(): void {
    this.assetsService.downloadProtectedAsset('DSCAL_User_Guide_V4.1.pdf');
  }

  confirmAndDownloadCoveredPorosityTemplate(): void {
    const dialogRef = this.dialog.open(ConfirmPopupComponent, {
      data: {
        title: 'Download Covered Porosity Template',
        message: 'Do you want to download the Covered Porosity Template?\nThe file will be saved to your Downloads folder.',
        confirmText: 'Download',
        cancelText: 'Cancel',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.assetsService.downloadProtectedAsset('DSCAL_Covered_Porosity_Template_V1.0.xlsx');
        this.snackbarService.openCustom({
          title: 'Download started',
          message: 'Please check your Downloads folder for the Covered Porosity Template file.',
          type: 'info',
        }, { duration: 7000 });
      }
    });
  }

  confirmAndDownload(): void {
    const dialogRef = this.dialog.open(ConfirmPopupComponent, {
      data: {
        title: 'Download Porous Phase Segmentation Tool',
        message: 'Do you want to download the Porous Phase Segmentation Tool?\nThe file will be saved to your Downloads folder.',
        confirmText: 'Download',
        cancelText: 'Cancel',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.assetsService.downloadProtectedAsset('DSCAL_Porous_Phase_Segmentation_Tool_V1_0.zip');
        this.snackbarService.openCustom({
          title: 'Download started',
          message: 'Please check your Downloads folder for the Porous Phase Segmentation Tool zip file.',
          type: 'info',
        }, { duration: 7000 });
      }
    });
  }
}
